import { addTotalEngagementMetric } from 'api/metrics'
import { SESSION_IS_EXPIRED } from 'const'
import debounce from 'lodash/debounce'
import { router } from 'routes/router'
import { routes } from 'routes/routes'
import { handleBlurOnIframe } from 'services/Metric/Metric.utils'
import { TimeEngagement, User } from 'types'
import {
  clearLocalStorage,
  formatMetricDate,
  getFromLocalStorage,
  saveToLocalStorage,
  USER_DATA,
} from 'utils'
import NotificationSys from '../NotificationSystem'
import { addTimeObjects, formatTotalTime, parseTotalEngagement } from '../page/utils'

const ONE_MIN = 60000
const ONE_SEC = 1000
const MAX_SESSION_DURATION = 15 // minutes
const PERIOD_INTERVAL = 3

class Timer {
  userSessionFrom: string | null
  idleIntervalId: NodeJS.Timer | null = null
  idleTime: number = 0
  periodCount: number = 0
  isSessionActive: boolean = false

  totalEngagementIntervalId: NodeJS.Timer | null = null
  totalEngagementValue: TimeEngagement = { hours: 0, minutes: 0, seconds: 0 }
  totalEngagementIntervalStartedAt: number | null = null

  constructor() {
    this.userSessionFrom = formatMetricDate()
    this.startIdleTimer()

    window.addEventListener('mousemove', debounce(this.resetIdleTimer, 300))
    window.addEventListener('keypress', debounce(this.resetIdleTimer, 300))
    window.addEventListener('scroll', debounce(this.resetIdleTimer, 300), true)

    window.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('focus', this.handleFocus)
    window.addEventListener('blur', this.handleBlur)

    window.addEventListener('unload', this.unload)
  }

  startIdleTimer = () => {
    if (this.idleIntervalId) {
      clearInterval(this.idleIntervalId)
    }
    this.idleIntervalId = setInterval(this.idleTimerIncrement, ONE_MIN)
  }

  stopIdleTimer = () => {
    if (this.idleIntervalId) {
      clearInterval(this.idleIntervalId)
    }
  }

  resetIdleTimer = () => {
    if (!this.isSessionActive) return
    this.idleTime = 0
  }

  idleTimerIncrement = () => {
    if (!this.isSessionActive) return

    this.idleTime++
    this.periodCount++

    if (this.periodCount % PERIOD_INTERVAL === 0 && this.periodCount !== 0) {
      if (this.userSessionFrom) {
        this.sendMetricToServer(this.userSessionFrom, formatMetricDate())
      }
      this.userSessionFrom = formatMetricDate()
    }

    if (this.idleTime === MAX_SESSION_DURATION - 1) {
      NotificationSys.showWarningWithTimer('You will be logged out in {time}.', 'Stay logged in', 1)
    }

    if (this.idleTime === MAX_SESSION_DURATION) {
      this.stopUserSession()
      clearLocalStorage()
      NotificationSys.showWarning(SESSION_IS_EXPIRED)
      router.navigate(routes.signIn)
    }
  }

  startTotalEngagementTimer = () => {
    if (this.totalEngagementIntervalId) {
      clearInterval(this.totalEngagementIntervalId)
    }

    let interval = ONE_MIN
    if (this.totalEngagementValue.hours === 0 && this.totalEngagementValue.minutes === 0) {
      interval = ONE_SEC
    }

    this.totalEngagementIntervalStartedAt = Date.now()
    this.totalEngagementIntervalId = setInterval(() => {
      this.renewLocalTotalEngagement(interval)
    }, interval)
  }

  stopTotalEngagementTimer = () => {
    if (this.totalEngagementIntervalId) {
      clearInterval(this.totalEngagementIntervalId)
      this.totalEngagementIntervalStartedAt = null
    }
  }

  setUserTotalEngagement = () => {
    const user = getFromLocalStorage(USER_DATA) as User | null
    const parsedEngagement = parseTotalEngagement(user?.totalEngagement)
    if (parsedEngagement) {
      this.totalEngagementValue = parsedEngagement
    }
  }

  renewLocalTotalEngagement = (interval: number) => {
    const currentUserData = getFromLocalStorage(USER_DATA) as User | null
    if (!currentUserData) return

    this.totalEngagementValue = addTimeObjects(this.totalEngagementValue, {
      hours: 0,
      minutes: interval === ONE_MIN ? 1 : 0,
      seconds: interval === ONE_SEC ? 1 : 0,
    })

    const { hours, minutes, seconds } = this.totalEngagementValue
    saveToLocalStorage(USER_DATA, {
      ...currentUserData,
      totalEngagement: `${hours}:${minutes}:${seconds}`,
    })
  }

  saveTotalEngagementAfterHidePage = () => {
    const currentUserData = getFromLocalStorage(USER_DATA) as User | null
    if (!currentUserData || !this.totalEngagementIntervalStartedAt) return

    const elapsedMs = Date.now() - this.totalEngagementIntervalStartedAt
    const additionalSeconds = Math.floor(elapsedMs / 1000)

    const updatedValue = addTimeObjects(this.totalEngagementValue, {
      hours: 0,
      minutes: Math.floor(additionalSeconds / 60),
      seconds: additionalSeconds % 60,
    })

    const { hours, minutes, seconds } = updatedValue

    saveToLocalStorage(USER_DATA, {
      ...currentUserData,
      totalEngagement: `${hours}:${minutes}:${seconds}`,
    })
  }

  getTotalEngagementText = () => {
    return formatTotalTime(this.totalEngagementValue)
  }

  startUserSession = () => {
    const user = getFromLocalStorage(USER_DATA) as User | null
    if (!user) return

    this.isSessionActive = true
    this.userSessionFrom = formatMetricDate()
    this.startIdleTimer()
    this.setUserTotalEngagement()
    this.startTotalEngagementTimer()
  }

  stopUserSession = () => {
    this.isSessionActive = false
    this.stopIdleTimer()
    this.saveTotalEngagementAfterHidePage()
    this.stopTotalEngagementTimer()
    if (this.userSessionFrom) {
      this.sendMetricToServer(this.userSessionFrom, formatMetricDate())
    }
    this.userSessionFrom = null
    this.periodCount = 0
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.stopUserSession()
      return
    }

    this.startUserSession()
  }

  handleFocus = () => {
    // if we were in iframe like YouTube and return to main window,
    // we shouldn't restart session
    if (this.isSessionActive) {
      return
    }

    this.startUserSession()
  }

  handleBlur = () => {
    handleBlurOnIframe(() => {
      this.stopUserSession()
    })
  }

  sendMetricToServer = (startDate: string, endDate: string) => {
    const user = getFromLocalStorage(USER_DATA) as User | null
    if (user) {
      addTotalEngagementMetric({
        userId: user.id,
        engagedFrom: startDate,
        engagedTo: endDate,
      })
    }
  }

  unload = () => {
    this.stopUserSession()
  }
}

export const timeMeasureSystem = new Timer()
